import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useScrollReveal } from "../hooks/useScrollReveal";

const PrivacyPolicyPage = () => {
  const headerReveal = useScrollReveal({ threshold: 0.1 });
  const contentReveal = useScrollReveal({ threshold: 0.05 });

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-24">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
            headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            🔐 Privacy Policy
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Last Updated: 22-04-2026
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-b from-white to-gray-50 py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div
            ref={contentReveal.ref}
            className={`max-w-[1020px] mx-auto bg-white/95 rounded-3xl p-6 sm:p-10 lg:p-12 shadow-soft ring-1 ring-gray-100 text-left transition-all duration-1000 ${
              contentReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <div className="pb-7 border-b border-gray-100">
              <p className="text-lg text-gray-600 leading-relaxed">
                Domigo ("we", "our", "us") is committed to safeguarding the privacy and personal information of our customers and their pets. This Privacy Policy outlines how we collect, use, disclose, and protect your information when you access our services, website, or communicate with us.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                1. Information We Collect
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We may collect and process the following categories of information:
              </p>

              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 pt-1 mb-2">
                1.1 Personal Information
              </h3>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Full name</li>
                <li>Contact number</li>
                <li>Email address</li>
                <li>Residential address</li>
              </ul>

              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 pt-2 mb-2">
                1.2 Pet Information
              </h3>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Pet name, breed, age, and gender</li>
                <li>Vaccination and medical history</li>
                <li>Dietary preferences and behavioral details</li>
              </ul>

              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 pt-2 mb-2">
                1.3 Service &amp; Booking Information
              </h3>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Booking details and service preferences</li>
                <li>Transaction and payment-related information</li>
                <li>Service history and usage patterns</li>
              </ul>

              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 pt-2 mb-2">
                1.4 Communication Data
              </h3>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Interactions via phone calls, WhatsApp, email, or website forms</li>
                <li>Feedback, queries, and support requests</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                2. Purpose of Data Collection
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Your information is collected and used for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>To provide, manage, and improve our pet care services</li>
                <li>To process bookings, payments, and service requests</li>
                <li>To communicate important updates, confirmations, and reminders</li>
                <li>To ensure the safety, health, and well-being of pets in our care</li>
                <li>To enhance customer experience and service quality</li>
                <li>To comply with applicable legal and regulatory requirements</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                3. Sharing and Disclosure of Information
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Domigo does not sell, rent, or trade your personal information.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We may share information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>With authorized staff and personnel for service delivery</li>
                <li>With veterinary professionals in case of medical necessity</li>
                <li>With trusted third-party service providers (e.g., payment gateways)</li>
                <li>When required by law, regulation, or legal process</li>
              </ul>
              <p className="text-lg text-gray-600 leading-relaxed">
                All third parties are obligated to maintain confidentiality and use the data solely for the intended purpose.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                4. Data Security
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, loss, misuse, or alteration.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                While we strive to use commercially acceptable means to protect your data, no system can guarantee absolute security.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                5. Data Retention
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We retain your information only for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Fulfill the purposes outlined in this policy</li>
                <li>Maintain service records</li>
                <li>Comply with legal, regulatory, and operational requirements</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                6. Your Rights
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate or incomplete information</li>
                <li>Request deletion of your personal data (subject to legal obligations)</li>
              </ul>
              <p className="text-lg text-gray-600 leading-relaxed">
                Requests can be made by contacting us using the details provided below.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                7. Communication &amp; Notifications
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                By using our services, you consent to receive service-related communications, including:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Booking confirmations</li>
                <li>Service updates</li>
                <li>Reminders and notifications</li>
              </ul>
              <p className="text-lg text-gray-600 leading-relaxed">
                You may opt out of non-essential or promotional communications at any time.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                8. Cookies and Website Usage
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our website may use cookies and similar technologies to enhance user experience, analyze traffic, and improve functionality. You can manage cookie preferences through your browser settings.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                9. Third-Party Services
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We may utilize third-party platforms for payments, communication, and analytics. These services operate under their own privacy policies, and Domigo is not responsible for their practices.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                10. Updates to This Policy
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Domigo reserves the right to update or modify this Privacy Policy at any time. Changes will be posted on this page with the revised "Last Updated" date.
              </p>
            </div>

            <div className="space-y-4 pt-7">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                11. Contact Information
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                For any questions, concerns, or requests regarding this Privacy Policy, please contact us:
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Domigo
                <br />
                Indore Bypass Road, near Kushabhau Thackrey Road,
                <br />
                Opposite Bharat Benz Showroom,
                <br />
                Indore, Madhya Pradesh – 452016
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">📞 +91 831 951 0854</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;